import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import NavbarMex from '../components/navbarMex';
import { utils, writeFile } from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';
import BotonWhatsapp from '../botonWhatsapp/botonWhatsapp';
import './TableComponent.css';
import { CheckCircleOutlined } from '@ant-design/icons';

const TableComponent = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [filterEstado, setFilterEstado] = useState('');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  // Añadir estados para el manifiesto
  const [showManifestModal, setShowManifestModal] = useState(false);
  const [manifestForm, setManifestForm] = useState({
    id_transportadora: '',
    fecha_inicio: new Date().toISOString().split('T')[0],
    fecha_fin: new Date().toISOString().split('T')[0]
  });
  const [manifestData, setManifestData] = useState(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  
  const usuario_id = localStorage.getItem('usuario_id');
  const navigate = useNavigate();

  const closePdfViewer = () => {
    setShowPdfViewer(false);
  };

  useEffect(() => {
    axios.get(`https://api.99envios.app/api/online/envios_completos/${usuario_id}`)
      .then(response => {
        const transformedData = response.data.map(item => ({
          ...item,
          codigo: item.codigo_sucursal,
          fecha_envio: formatDateForDisplay(item.fecha_envio),
          numero_guia: item.numero_de_guia,
          producto: item.Producto,
          destinatario: item.nombre_destinatario,
          direccion: item.direccion_destinatario,
          telefono: item.telefono_destinatario,
          ciudad: item.ciudad_destino,
          valor: item.valor_comercial,
          valor_servicio: item.valor_servicio,
          aplica_contrapago: item.aplica_contrapago,
          estado: item.estado_del_envio,
          seguro_99: item.seguro_99,
          seguro_99plus: item.seguro_99_plus,
          fecha_actualizacion: formatDateForDisplay(item.fecha_actualizacion),
          pdf: item.pdf
        }));
        // Sort by tracking number in descending order
        transformedData.sort((a, b) => {
          // Convert to strings and compare in reverse order
          const guiaA = String(a.numero_guia);
          const guiaB = String(b.numero_guia);
          return guiaB.localeCompare(guiaA, undefined, {numeric: true});
        });
        setData(transformedData);
        // Initialize sort config to maintain descending order by tracking number
        setSortConfig({ key: 'numero_guia', direction: 'descending' });
      })
      .catch(error => console.error('Error fetching data: ', error));
  }, [usuario_id]);

  // Helper function to format dates consistently
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Return original if invalid
    return date.toISOString().split('T')[0];
  };

  // Helper function to compare dates safely
  const compareDates = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    
    // Check if either date is invalid
    if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
      return false;
    }
    
    // Reset time portion for date-only comparison
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    
    return d1.getTime() === d2.getTime();
  };

  // Decodificación y visualización del PDF en base64
  const handlePdfView = (pdfUrl) => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank'); // Abre la URL del PDF en una nueva pestaña
    } else {
      alert('PDF no disponible para este envío');
    }
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleDateChange = (type, value) => {
    setDateRange(prev => ({ ...prev, [type]: value }));
    setCurrentPage(1); // Reset to first page when changing filters
  };

  const sortedData = React.useMemo(() => {
    let sortableData = [...data];
    if (sortConfig.key !== '') {
      sortableData.sort((a, b) => {
        if (sortConfig.key === 'fecha_envio' || sortConfig.key === 'fecha_actualizacion') {
          const dateA = new Date(a[sortConfig.key]);
          const dateB = new Date(b[sortConfig.key]);
          return sortConfig.direction === 'ascending' 
            ? dateA - dateB
            : dateB - dateA;
        }
        
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableData;
  }, [data, sortConfig]);

  const filteredData = sortedData.filter(item => {
    // Search filter
    const matchesSearch = Object.values(item).some(val => 
      val && val.toString().toLowerCase().includes(search.toLowerCase())
    );
    
    // Estado filter
    const matchesEstado = filterEstado === '' || 
      (filterEstado === 'Entregado' && (item.estado === 'Entregada' || item.estado === 'Entregado')) ||
      item.estado === filterEstado;
    
    // Date filter
    let matchesDate = true;
    if (dateRange.startDate || dateRange.endDate) {
      const itemDate = new Date(item.fecha_envio);
      itemDate.setHours(0, 0, 0, 0);

      if (dateRange.startDate) {
        const startDate = new Date(dateRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        if (itemDate < startDate) matchesDate = false;
      }

      if (dateRange.endDate && matchesDate) {
        const endDate = new Date(dateRange.endDate);
        endDate.setHours(0, 0, 0, 0);
        if (itemDate > endDate) matchesDate = false;
      }
    }

    return matchesSearch && matchesEstado && matchesDate;
  });

  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredData.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(filteredData.length / entriesPerPage);

  const exportToExcel = () => {
    // Crear una copia de los datos filtrados excluyendo la columna pdf
    const exportData = filteredData.map(item => {
      const { pdf, ...rest } = item;
      return rest;
    });

    const worksheet = utils.json_to_sheet(exportData);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Envíos Completos");

    // Ajustar el ancho de las columnas
    const wscols = [
      {wch: 15}, // codigo
      {wch: 12}, // fecha_envio
      {wch: 15}, // numero_guia
      {wch: 20}, // producto
      {wch: 25}, // destinatario
      {wch: 30}, // direccion
      {wch: 15}, // telefono
      {wch: 15}, // ciudad
      {wch: 12}, // valor
      {wch: 12}, // valor servicio
      {wch: 12}, // aplica contrapago
      {wch: 15}, // estado
      {wch: 12}, // seguro_99
      {wch: 12}, // seguro_99plus
      {wch: 12}  // fecha_actualizacion
    ];

    worksheet['!cols'] = wscols;

    writeFile(workbook, `Envios_Completos_${formatDateForDisplay(new Date())}.xlsx`);
  };

  const columns = useMemo(
    () => [
      // ... otras columnas existentes ...
      {
        Header: "Valor Servicio",
        accessor: "valor_servicio",
        Cell: ({ value }) => (
          <span>
            ${new Intl.NumberFormat('es-CO').format(value)}
          </span>
        )
      },
      {
        Header: "Aplica Contrapago",
        accessor: "aplica_contrapago", 
        Cell: ({ value }) => {
          return (
            <span>
              {parseInt(value) === 1 ? "No" : "Si"}
            </span>
          );
        }
      },
      // ... otras columnas existentes ...
    ],
    []
  );

  // Funciones para manejo del manifiesto
  const handleManifestSubmit = async (e) => {
    e.preventDefault();
    setManifestLoading(true);
    try {
      const codigo_sucursal = localStorage.getItem('codigoSucursal');
      const requestData = {
        ...manifestForm,
        codigo_sucursal
      };
      
      const response = await axios.post('https://99envios.app/api/online/generar-manifiesto/pdf', requestData);
      setManifestData(response.data);
      setManifestLoading(false);
    } catch (error) {
      console.error('Error generating manifest:', error);
      alert('Error al generar el manifiesto');
      setManifestLoading(false);
    }
  };

  const closeManifestModal = () => {
    setShowManifestModal(false);
    setManifestData(null);
  };

  const openPdfInNewTab = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const generatePDF = () => {
    if (!manifestData || !manifestData.datos_gias) return;
    
    try {
      // Create a new PDF document - explicitly add jsPDF options to avoid errors
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      // Add title
      doc.setFontSize(18);
      doc.setTextColor(76, 69, 177); // #4c45b1
      doc.text(`Manifiesto de ${manifestData.transportadora || 'Envíos'}`, 14, 15);
      
      // Add date
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const today = new Date().toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      doc.text(`Fecha: ${today}`, 14, 22);
      
      // Add transportadora info
      doc.setFontSize(12);
      doc.setTextColor(76, 69, 177);
      doc.text(`Transportadora: ${manifestData.transportadora || ''}`, 14, 29);
      
      // Enfoque más simplificado para dibujar la tabla
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const startY = 40;
      let currentY = startY;
      const rowHeight = 10;
      
      // Definimos encabezados y anchos
      const columns = [
        { header: "Número", width: 25 },
        { header: "Fecha", width: 25 },
        { header: "Flete", width: 25 },
        { header: "Contrapago", width: 25 },
        { header: "Contenido", width: 50 },
        { header: "Destinatario", width: 40 },
        { header: "Teléfono", width: 30 },
        { header: "Peso", width: 20 }
      ];
      
      // Función para dibujar una fila
      const drawRow = (items, isHeader = false) => {
        let x = margin;
        
        // Configurar estilo según sea encabezado o fila de datos
        if (isHeader) {
          doc.setFillColor(76, 69, 177);
          doc.setTextColor(255, 255, 255);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
        } else {
          doc.setTextColor(0, 0, 0); // Asegurar que el texto sea negro
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(7);
        }
        
        // Dibujar cada celda en la fila
        columns.forEach((column, i) => {
          // Dibujar el rectángulo de fondo
          doc.rect(x, currentY, column.width, rowHeight, isHeader ? 'F' : 'S');
          
          // Calcular posición de texto
          let textX = x + 2; // Margen interno
          const textY = currentY + 6; // Centrado vertical aproximado
          
          // Si es un valor monetario, alinear a la derecha
          if ((i === 2 || i === 3) && !isHeader) {
            // Para valores monetarios, alineamos a la derecha
            const text = items[i];
            const textWidth = doc.getStringUnitWidth(text) * doc.internal.getFontSize() / doc.internal.scaleFactor;
            textX = x + column.width - 2 - textWidth;
          }
          
          // Escribir texto
          doc.text(items[i], textX, textY);
          x += column.width;
        });
        
        currentY += rowHeight;
        
        // Añadir nueva página si es necesario
        if (currentY > pageHeight - margin * 2) {
          doc.addPage();
          currentY = margin;
          
          // Si estamos en una nueva página, dibujamos el encabezado nuevamente
          if (!isHeader) {
            const headers = columns.map(col => col.header);
            drawRow(headers, true);
          }
        }
      };
      
      // Dibujar encabezado
      const headers = columns.map(col => col.header);
      drawRow(headers, true);
      
      // Dibujar filas de datos
      manifestData.datos_gias.forEach(guia => {
        try {
          const destinatario = guia.destinatario 
            ? `${guia.destinatario.nombre || ''} ${guia.destinatario.primer_apellido || ''}`.trim()
            : 'N/A';
          
          const row = [
            guia.numero_preenvio || 'N/A',
            (guia.fecha_creacion || '').substr(0, 10) || 'N/A',
            `$${parseFloat(guia.valor_flete || 0).toLocaleString('es-CO')}`,
            `$${parseFloat(guia.valor_contrapago || 0).toLocaleString('es-CO')}`,
            (guia.dice_contener || '').substr(0, 25) || 'N/A', // Limitamos longitud
            destinatario.substr(0, 20) || 'N/A', // Limitamos longitud
            (guia.destinatario?.telefono || 'N/A').substr(0, 12),
            `${guia.peso || 0} kg`
          ];
          
          drawRow(row, false);
        } catch (error) {
          console.error('Error al procesar fila:', error);
        }
      });
      
      // Añadir resumen al final
      currentY += 5; // Espacio después de la tabla
      
      doc.setFontSize(10);
      doc.setTextColor(50, 50, 50);
      doc.text(`Total Guías: ${manifestData.datos_gias.length}`, margin, currentY + 5);
      
      // Calculate totals safely with error handling
      let totalFlete = 0;
      let totalContrapago = 0;
      
      manifestData.datos_gias.forEach(guia => {
        try {
          totalFlete += parseFloat(guia.valor_flete || 0);
          totalContrapago += parseFloat(guia.valor_contrapago || 0);
        } catch (error) {
          // Skip problematic values
        }
      });
      
      doc.text(`Total Valor Flete: $${totalFlete.toLocaleString('es-CO')}`, margin, currentY + 12);
      doc.text(`Total Valor Contrapago: $${totalContrapago.toLocaleString('es-CO')}`, margin, currentY + 19);
      
      // Add footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.text(
          `Página ${i} de ${pageCount} - Generado por 99envios - ${today}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      }
      
      // Save the PDF with safer filename
      const safeName = manifestData.transportadora ? 
                        manifestData.transportadora.replace(/[^a-zA-Z0-9]/g, '_') : 
                        'Envios';
      doc.save(`Manifiesto_${safeName}_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error detallado al generar el PDF:', error);
      alert('Error al generar el PDF: ' + (error.message || 'Error desconocido'));
    }
  };

  const toggleRowSelection = (item) => {
    if (!item.pdf) return; // Don't select rows without PDF
    setSelectedRows(prev => {
      if (prev.find(row => row.numero_guia === item.numero_guia)) {
        return prev.filter(row => row.numero_guia !== item.numero_guia);
      } else {
        return [...prev, item];
      }
    });
  };

  const mergePDFs = async () => {
    try {
      const mergedPdf = await PDFDocument.create();
      
      // Fetch and merge each PDF
      for (const row of selectedRows) {
        if (!row.pdf) continue;
        
        // Fetch the PDF
        const pdfBytes = await fetch(row.pdf).then(res => res.arrayBuffer());
        const pdf = await PDFDocument.load(pdfBytes);
        
        // Copy pages
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => {
          mergedPdf.addPage(page);
        });
      }
      
      // Save the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      
      // Create a blob and download
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Guias_Combinadas_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error al combinar PDFs:', error);
      alert('Error al combinar los PDFs');
    }
  };

  const openSelectedPdfs = () => {
    const pdfs = selectedRows.map(row => row.pdf).filter(Boolean);
    if (pdfs.length > 0) {
      if (pdfs.length === 1) {
        window.open(pdfs[0], '_blank');
      } else {
        mergePDFs();
      }
    } else {
      alert('No hay PDFs seleccionados para visualizar');
    }
  };

  const getStatusBadgeClass = (estado) => {
  switch ((estado ?? '').toLowerCase()) {
    case 'creado':
      return 'status-badge status-creado';
    case 'confirmado por cliente':
      return 'status-badge status-confirmado';
    case 'centro acopio':
      return 'status-badge status-centro-acopio';
    case 'entregada':
      return 'status-badge status-entregado';
    case 'intento de entrega':
      return 'status-badge status-intento-entrega';
    case 'transito nacional':
      return 'status-badge status-transito-nacional';
    case 'reclame en oficina':
      return 'status-badge status-reclamar-oficina';
    default:
      return 'status-badge status-desconocido';
  }
};

  return (
    <>
      <NavbarMex title="Envios Completos" />
      <div className="table-containerEC">
        <div className="controls-container" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          alignItems: 'center',
          padding: '20px',
          gap: '20px',
          backgroundColor: '#f8f9fa'
        }}>
          <button 
            onClick={() => navigate('/dashboard99/historico-envios')} 
            style={{
              padding: '10px 20px',
              backgroundColor: '#4c45b1',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Históricos Envios
          </button>
          
          <button 
            onClick={() => setShowManifestModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4c45b1',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Generar Manifiesto
          </button>
          
          <button 
            onClick={exportToExcel}
            style={{
              padding: '10px 20px',
              backgroundColor: '#217346',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Exportar a Excel
          </button>
          
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <label style={{ fontWeight: 'bold' }}>Mostrar:</label>
            <select
              value={entriesPerPage}
              onChange={(e) => setEntriesPerPage(parseInt(e.target.value))}
              style={{
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #ddd'
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          
          <div>
            <input
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={handleSearch}
              style={{
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                width: '180px'
              }}
            />
          </div>
          
          <div>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
              style={{
                padding: '10px',
                borderRadius: '5px',
                border: '1px solid #ddd',
                width: '180px'
              }}
            >
              <option value="">Todos los estados</option>
              <option value="Entregado">Entregado</option>
              <option value="Creado">Creado</option>
              <option value="Envío programado">Envío programado</option>
            </select>
          </div>
          
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <label style={{ fontWeight: 'bold' }}>Desde:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              max={dateRange.endDate || undefined}
              style={{
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #ddd'
              }}
            />
          </div>
          
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <label style={{ fontWeight: 'bold' }}>Hasta:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              min={dateRange.startDate || undefined}
              style={{
                padding: '8px',
                borderRadius: '5px',
                border: '1px solid #ddd'
              }}
            />
          </div>

          {selectedRows.length > 0 && (
            <button 
              onClick={openSelectedPdfs}
              style={{
                padding: '10px 20px',
                backgroundColor: '#4c45b1',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginBottom: '20px'
              }}
            >
              {selectedRows.length === 1 ? 'Ver PDF' : 'Descargar PDFs Unidos'} ({selectedRows.length})
            </button>
          )}
        </div>

        <div className="table-wrapper">
          <table className="custom-table">
            <thead>
              <tr>
                <th style={{maxWidth:"40px"}}></th>
                <th onClick={() => handleSort('codigo')}>Código</th>
                <th onClick={() => handleSort('fecha_envio')}>Fecha envío</th>
                <th onClick={() => handleSort('numero_guia')}>Número de guía</th>
                <th onClick={() => handleSort('producto')}>Producto</th>
                <th onClick={() => handleSort('destinatario')}>Destinatario</th>
                <th onClick={() => handleSort('direccion')}>Dirección</th>
                <th onClick={() => handleSort('telefono')}>Teléfono</th>
                <th onClick={() => handleSort('ciudad')}>Ciudad</th>
                <th onClick={() => handleSort('valor')}>Valor</th>
                <th onClick={() => handleSort('valor_servicio')}>Flete</th>
                <th onClick={() => handleSort('aplica_contrapago')}>Credito</th>
                <th onClick={() => handleSort('estado')}>Estado</th>
                <th onClick={() => handleSort('seguro_99')}>Seguro 99</th>
                <th onClick={() => handleSort('seguro_99plus')}>Seguro 99 Plus</th>
                <th onClick={() => handleSort('fecha_actualizacion')}>Fecha Actualización</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {currentEntries.map((item, index) => (
                <tr key={index}>
                  <td style={{ width: '30px', padding: '0 5px' }}>
                    <input
                      type="checkbox"
                      checked={!!selectedRows.find(row => row.numero_guia === item.numero_guia)}
                      onChange={() => toggleRowSelection(item)}
                      disabled={!item.pdf}
                      style={{ 
                        cursor: item.pdf ? 'pointer' : 'not-allowed',
                        width: '15px',
                        height: '15px'
                      }}
                    />
                  </td>
                  <td>{item.codigo}</td>
                  <td>{item.fecha_envio}</td>
                  <td>{item.numero_guia}</td>
                  <td className="ellipsis-cell" title={item.producto}>{item.producto}</td>
                  <td>{item.destinatario}</td>
                  <td className="ellipsis-cell" title={item.direccion}>{item.direccion}</td>
                  <td>{item.telefono}</td>
                  <td className="ellipsis-cell" title={item.ciudad}>{item.ciudad}</td>
                  <td className="numbers-td-color">${item.valor.toLocaleString()}</td>
                  <td className="numbers-td">${item.valor_servicio.toLocaleString()}</td>
                  <td className="center-column">{parseInt(item.aplica_contrapago) === 1 ? "No" : "Si"}</td>
                  <td className="center-column">
                    <span className={getStatusBadgeClass(item.estado)}>
                      {item.estado}
                     </span>
                  </td>
                  <td className="center-column">{parseInt(item.seguro_99) === 1 ?  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />: <span style={{ color: '#d9d9d9' }}>Desactivado</span>}</td>
                  <td className="center-column">{parseInt(item.seguro_99plus) === 1 ?  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />: <span style={{ color: '#d9d9d9' }}>Desactivado</span>}</td>
                  <td>{item.fecha_actualizacion}</td>
                  <td>
                    {item.pdf ? (
                      <button
                        onClick={() => handlePdfView(item.pdf)}
                        className="pdf-button"
                      >
                        Ver PDF
                      </button>
                    ) : (
                      <span className="no-pdf">No disponible</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            className="page-button"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            {'<<'}
          </button>
          <button
            className="page-button"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
          >
            {'<'}
          </button>

          {[...Array(totalPages)].map((_, index) => {
            const pageNumber = index + 1;
            // Show first page, last page, current page, and one page before and after current
            if (
              pageNumber === 1 ||
              pageNumber === totalPages ||
              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
            ) {
              return (
                <button
                  key={index}
                  className={`page-button ${currentPage === pageNumber ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            } else if (
              (pageNumber === currentPage - 2 && currentPage > 3) ||
              (pageNumber === currentPage + 2 && currentPage < totalPages - 2)
            ) {
              return <span key={index} className="pagination-ellipsis">...</span>;
            }
            return null;
          })}

          <button
            className="page-button"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            {'>'}
          </button>
          <button
            className="page-button"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            {'>>'}
          </button>
        </div>
      </div>
      <BotonWhatsapp />

      {/* Modal de Manifiesto */}
      {showManifestModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ 
            width: manifestData ? '85%' : '450px', 
            maxWidth: '1200px', 
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#4c45b1' }}>
                {manifestData ? 'Manifiesto Generado' : 'Generar Manifiesto'}
              </h2>
              <button 
                onClick={closeManifestModal} 
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                &times;
              </button>
            </div>

            {!manifestData ? (
              <form onSubmit={handleManifestSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label htmlFor="transportadora" style={{ fontWeight: 'bold' }}>Seleccionar Transportadora:</label>
                  <select
                    id="transportadora"
                    value={manifestForm.id_transportadora}
                    onChange={(e) => setManifestForm({...manifestForm, id_transportadora: e.target.value})}
                    required
                    style={{ 
                      padding: '10px', 
                      borderRadius: '5px',
                      border: '1px solid #ddd'
                    }}
                  >
                    <option value="">Seleccionar Transportadora</option>
                    <option value="1">Interrapidisimo</option>
                    <option value="2">Servientrega</option>
                    <option value="3">TCC</option>
                    <option value="4">Coordinadora</option>
                  </select>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label htmlFor="fecha_inicio" style={{ fontWeight: 'bold' }}>Fecha Inicio:</label>
                  <input
                    id="fecha_inicio"
                    type="date"
                    value={manifestForm.fecha_inicio}
                    onChange={(e) => setManifestForm({...manifestForm, fecha_inicio: e.target.value})}
                    required
                    style={{ 
                      padding: '10px', 
                      borderRadius: '5px',
                      border: '1px solid #ddd'
                    }}
                  />
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label htmlFor="fecha_fin" style={{ fontWeight: 'bold' }}>Fecha Fin:</label>
                  <input
                    id="fecha_fin"
                    type="date"
                    value={manifestForm.fecha_fin}
                    onChange={(e) => setManifestForm({...manifestForm, fecha_fin: e.target.value})}
                    required
                    style={{ 
                      padding: '10px', 
                      borderRadius: '5px',
                      border: '1px solid #ddd'
                    }}
                  />
                </div>
                
                <div className="modal-buttons" style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '10px',
                  marginTop: '15px' 
                }}>
                  <button 
                    type="submit" 
                    disabled={manifestLoading}
                    style={{ 
                      padding: '10px 20px',
                      backgroundColor: manifestLoading ? '#cccccc' : '#4c45b1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: manifestLoading ? 'default' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {manifestLoading ? 'Generando...' : 'Generar'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeManifestModal}
                    style={{ 
                      padding: '10px 20px',
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            ) : (
              <div className="manifest-results">
                <div className="manifest-header" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '20px',
                  padding: '15px',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef'
                }}>
                  <div className="manifest-logos" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    {manifestData.logo_transportadora && (
                      <img 
                        src={manifestData.logo_transportadora} 
                        alt="Logo Transportadora" 
                        height="60" 
                        style={{ objectFit: 'contain' }}
                      />
                    )}
                    {manifestData.logo_99envios && (
                      <img 
                        src={manifestData.logo_99envios} 
                        alt="Logo 99envios" 
                        height="45" 
                        style={{ objectFit: 'contain' }}
                      />
                    )}
                  </div>
                  <h3 style={{ 
                    color: '#4c45b1', 
                    fontSize: '22px',
                    margin: '0'
                  }}>
                    Manifiesto {manifestData.transportadora || 'de Envíos'}
                  </h3>
                  <div style={{ minWidth: '80px' }}></div> {/* Spacer for balance */}
                </div>
                
                {manifestData.datos_gias && manifestData.datos_gias.length > 0 ? (
                  <div className="manifest-guias">
                    <h4 style={{ 
                      marginBottom: '15px',
                      color: '#333',
                      fontSize: '18px',
                      borderBottom: '2px solid #4c45b1',
                      paddingBottom: '10px'
                    }}>
                      Guías incluidas en el manifiesto
                    </h4>
                    <div className="table-wrapper" style={{ 
                      maxHeight: '50vh', 
                      overflowY: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #e9ecef'
                    }}>
                      <table className="custom-table" style={{ 
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#4c45b1', color: 'white' }}>
                          <tr>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Número Preenvío</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Fecha Creación</th>
                            <th style={{ padding: '12px 15px', textAlign: 'right' }}>Valor Flete</th>
                            <th style={{ padding: '12px 15px', textAlign: 'right' }}>Valor Contrapago</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Dice Contener</th>
                            <th style={{ padding: '12px 15px', textAlign: 'right' }}>Valor Declarado</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Destinatario</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Teléfono</th>
                            <th style={{ padding: '12px 15px', textAlign: 'left' }}>Dirección</th>
                            <th style={{ padding: '12px 15px', textAlign: 'center' }}>Peso</th>
                          </tr>
                        </thead>
                        <tbody>
                          {manifestData.datos_gias.map((guia, index) => (
                            <tr key={index} style={{ 
                              backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white',
                              borderBottom: '1px solid #e9ecef'
                            }}>
                              <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.numero_preenvio}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.fecha_creacion}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'right' }}>${parseFloat(guia.valor_flete || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'right' }}>${parseFloat(guia.valor_contrapago || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.dice_contener}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'right' }}>${parseFloat(guia.valor_declarado || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.destinatario ? `${guia.destinatario.nombre} ${guia.destinatario.primer_apellido || ''}` : 'N/A'}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.destinatario?.telefono || 'N/A'}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'left' }}>{guia.destinatario?.direccion || 'N/A'}</td>
                              <td style={{ padding: '10px 15px', textAlign: 'center' }}>{guia.peso} kg</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <div className="manifest-summary" style={{ 
                      marginTop: '20px',
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        backgroundColor: '#e9ecef'
                      }}>
                        <strong style={{ marginRight: '10px' }}>Total Guías:</strong> 
                        <span style={{ fontSize: '18px', color: '#4c45b1' }}>{manifestData.datos_gias.length}</span>
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        backgroundColor: '#e9ecef'
                      }}>
                        <strong style={{ marginRight: '10px' }}>Total Valor Flete:</strong> 
                        <span style={{ fontSize: '18px', color: '#4c45b1' }}>
                          ${manifestData.datos_gias.reduce((sum, guia) => sum + parseFloat(guia.valor_flete || 0), 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      
                      <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 15px',
                        borderRadius: '5px',
                        backgroundColor: '#e9ecef'
                      }}>
                        <strong style={{ marginRight: '10px' }}>Total Valor Contrapago:</strong> 
                        <span style={{ fontSize: '18px', color: '#4c45b1' }}>
                          ${manifestData.datos_gias.reduce((sum, guia) => sum + parseFloat(guia.valor_contrapago || 0), 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ 
                    padding: '40px', 
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef'
                  }}>
                    <p style={{ fontSize: '18px', color: '#6c757d' }}>No hay guías disponibles para este manifiesto.</p>
                  </div>
                )}
                
                <div className="modal-buttons" style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '15px',
                  marginTop: '25px' 
                }}>
                 
                  <button 
                    type="button" 
                    onClick={generatePDF}
                    style={{ 
                      padding: '12px 24px',
                      backgroundColor: '#217346', // Color verde de Excel
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>⬇️</span> Descargar PDF
                  </button>
                  <button 
                    type="button" 
                    onClick={closeManifestModal}
                    style={{ 
                      padding: '12px 24px',
                      backgroundColor: '#f5f5f5',
                      color: '#333',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      cursor: 'pointer'
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal del visor de PDFs */}
      {showPdfViewer && selectedRows.length > 0 && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            width: '95%',
            height: '90vh',
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '20px',
            position: 'relative'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px'
            }}>
              <h3 style={{ margin: 0 }}>PDFs Seleccionados ({selectedRows.length})</h3>
              <button
                onClick={closePdfViewer}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                &times;
              </button>
            </div>
            <div style={{
              display: 'flex',
              height: 'calc(100% - 50px)',
              gap: '10px'
            }}>
              <div style={{
                width: '200px',
                overflowY: 'auto',
                borderRight: '1px solid #ddd',
                padding: '10px'
              }}>
                {selectedRows.map((row, index) => (
                  <div
                    key={index}
                    style={{
                      padding: '10px',
                      marginBottom: '5px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontWeight: 'bold' }}>Guía: {row.numero_guia}</div>
                    <div style={{ fontSize: '12px' }}>{row.destinatario}</div>
                  </div>
                ))}
              </div>
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
              }}>
                {selectedRows.map((row, index) => (
                  <iframe
                    key={index}
                    src={row.pdf}
                    style={{
                      width: '100%',
                      height: `${100 / selectedRows.length}%`,
                      border: '1px solid #ddd',
                      borderRadius: '4px'
                    }}
                    title={`PDF ${row.numero_guia}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TableComponent;